export type CheckoutUpgradeIntent = {
  tier: 'creator' | 'pro' | 'business';
  billingCycle: 'monthly' | 'annual';
  source: 'signup' | 'upgrade';
  createdAt: number;
  userId?: string;
  trial?: boolean;
};

const STORAGE_KEY = 'socialforge.checkout-upgrade-intent';
const MAX_AGE_MS = 1000 * 60 * 60 * 24; // 24 hours

const hasStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const normalizeIntent = (value: unknown): CheckoutUpgradeIntent | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const tierValue = typeof (value as { tier?: unknown }).tier === 'string'
    ? (value as { tier: string }).tier.toLowerCase()
    : '';
  const billingCycleValue = typeof (value as { billingCycle?: unknown }).billingCycle === 'string'
    ? (value as { billingCycle: string }).billingCycle.toLowerCase()
    : 'monthly';
  const createdAtValue = Number((value as { createdAt?: unknown }).createdAt);
  const sourceValue = typeof (value as { source?: unknown }).source === 'string'
    ? (value as { source: string }).source.toLowerCase()
    : 'upgrade';
  const trialValue = (value as { trial?: unknown }).trial === true;

  if (tierValue !== 'creator' && tierValue !== 'pro' && tierValue !== 'business') {
    return null;
  }

  if (!Number.isFinite(createdAtValue) || createdAtValue <= 0) {
    return null;
  }

  return {
    tier: tierValue,
    billingCycle: billingCycleValue === 'annual' ? 'annual' : 'monthly',
    source: sourceValue === 'signup' ? 'signup' : 'upgrade',
    createdAt: createdAtValue,
    trial: trialValue,
    userId: typeof (value as { userId?: unknown }).userId === 'string'
      ? (value as { userId: string }).userId
      : undefined,
  };
};

export const saveCheckoutUpgradeIntent = (intent: Omit<CheckoutUpgradeIntent, 'createdAt'>) => {
  if (!hasStorage()) {
    return;
  }

  try {
    const payload: CheckoutUpgradeIntent = {
      ...intent,
      createdAt: Date.now(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('Failed to store checkout intent', error);
  }
};

export const loadCheckoutUpgradeIntent = (userId?: string | null): CheckoutUpgradeIntent | null => {
  if (!hasStorage()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = normalizeIntent(JSON.parse(raw));
    if (!parsed) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    if (Date.now() - parsed.createdAt > MAX_AGE_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    if (userId && parsed.userId && parsed.userId !== userId) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn('Failed to load checkout intent', error);
    return null;
  }
};

export const clearCheckoutUpgradeIntent = () => {
  if (!hasStorage()) {
    return;
  }

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear checkout intent', error);
  }
};

export const buildUpgradeCheckoutPath = (
  tier: CheckoutUpgradeIntent['tier'],
  billingCycle: CheckoutUpgradeIntent['billingCycle'],
  source: CheckoutUpgradeIntent['source'] = 'upgrade',
  trial = false,
) => {
  const params = new URLSearchParams({
    upgradeTier: tier,
    billingCycle,
    source,
  });

  if (trial) {
    params.set('trial', 'true');
  }

  return `/billing/checkout/start?${params.toString()}`;
};
